import { detectLocale } from '../i18n';
/**
 * Set `<html lang>` from the resolved shell locale, before first paint.
 *
 * ## Why this is shared rather than four lines in each `main.tsx`
 *
 * `apps/mobile` shipped this inline under an architect ruling (2026-08-26,
 * `decisions/architect/2026-08-26-mobile-runtime-manifest-locale-ruling.md`) and the
 * five sibling surfaces on this same package never inherited it — they still declare a
 * hardcoded `lang="ru"` in static HTML while `detectLocale()` renders English for an
 * English user. That is a propagation failure across `packages/app-core` consumers, and
 * a monorepo is supposed to make it impossible, so the fix is one exported call rather
 * than five copies of the same four lines.
 *
 * ⛔ **The manifest swap is deliberately NOT in here.** Mobile also rewrites its
 * `<link rel="manifest">` href, which is PWA-specific: the other surfaces have no
 * manifest, and folding it in would either no-op confusingly or invite someone to add a
 * manifest to satisfy the helper. Mobile keeps that step alongside this call, using the
 * `Locale` returned here so the decision is still made exactly once.
 *
 * ## Why it returns the locale
 *
 * So a caller that needs the same decision for a second contract (mobile's manifest)
 * does not call `detectLocale()` twice. Two calls would be two decisions, and a resolver
 * reading `navigator.language` and Telegram initData is not guaranteed to be stable
 * across a boot — the whole point of the architect ruling is that ONE shell-locale
 * decision drives every browser contract.
 *
 * ## `lang` is not decoration
 *
 * It is the input a screen reader uses to select voice and pronunciation rules, and it
 * drives translation offers, hyphenation, spellcheck and `:lang()`. A document that says
 * `ru` while rendering English is read to that user in Russian phonetics — and that
 * population is exactly who the attribute exists for.
 *
 * ⚠️ **No Lighthouse audit can catch a wrong value here, including the accessibility gate
 * armed at `error`/0.9.** `html-has-lang` checks the attribute is PRESENT and `valid-lang`
 * checks it is well-formed; `lang="ru"` serving English passes both, because neither audit
 * has any view of the content's actual language. A green a11y score is not evidence about
 * this property, so do not let one stand in for the agreement test.
 *
 * ## ⛔ Why this takes an explicit `locale` — the bug that made the parameter necessary
 *
 * The first version of this helper had no parameter and always wrote `detectLocale()`. That
 * is correct only for a surface whose COPY also comes from `detectLocale()`. Two do not:
 *
 * ```
 * apps/admin-tenant/src/i18n.ts     export function getMessages(locale: Locale = 'ru')
 * apps/admin-platform/src/i18n.ts   export function getMessages(locale: Locale = 'ru')
 * ```
 *
 * Both always render Russian. `detectLocale()` returns `'en'` for any `en-*` browser, so the
 * parameterless helper set `lang="en"` over Russian copy — measured in a real browser, and
 * **worse than the hardcoded `lang="ru"` it replaced**, which on those two surfaces was the
 * correct value rather than a fallback.
 *
 * ⭐ The property under contract is AGREEMENT between the attribute and the rendered copy —
 * not "the attribute equals `detectLocale()`". Those coincide only where the copy is
 * detection-driven. Product's ruling says so explicitly: *"Product requires agreement, not
 * one implementation tactic"*
 * (`decisions/product/2026-08-30-document-lang-mirrors-ui-locale.md`, acceptance #3).
 *
 * So: **pass the locale your surface actually renders.** The default keeps the
 * detection-driven surfaces (web-ui, desktop, mini-app, mobile) at one call and no argument;
 * a surface with its own fixed dictionary passes that dictionary's locale. Making the admin
 * panels honour `detectLocale()` instead would change what admin users SEE — a product
 * decision, not a tactic, and deliberately not taken here.
 */
export function applyDocumentLocale(locale = detectLocale()) {
    document.documentElement.lang = locale;
    return locale;
}
/**
 * Read back the locale this surface ALREADY DECIDED, from `<html lang>`.
 *
 * ## Why a reader exists at all, rather than callers calling `detectLocale()`
 *
 * `<html lang>` is not only an accessibility contract — after `applyDocumentLocale()` runs it is
 * the surface's one recorded locale decision, and anything else that needs the same answer should
 * READ it rather than make a second one. `apps/web-ui/src/routes/admin-tenant/i18n.ts` argues this
 * at length for a nested dictionary (*"A nested dictionary must FOLLOW the root's decision, not
 * make a second one … Deriving from it makes root/section disagreement unrepresentable rather than
 * merely tested for"*) and derives its `surfaceLocale()` exactly this way. The `/v1` locale request
 * header is the same kind of consumer, so it gets the same treatment instead of a third private
 * copy of these four lines.
 *
 * ⛔ **Not interchangeable with `detectLocale()`, and the difference is the whole point.**
 * `detectLocale()` re-runs the resolver — `navigator.language`, Telegram launch bytes, the SDK
 * global — and `applyDocumentLocale`'s docblock above already records that two calls are two
 * decisions. It is also simply WRONG for a surface whose copy is fixed: `apps/admin-tenant` and
 * `apps/admin-platform` render `UI_LOCALE = 'ru'` and call `applyDocumentLocale(UI_LOCALE)`, so
 * this reader returns `ru` for them (correct) where `detectLocale()` would return `en` on an
 * English browser (wrong, and invisible until a user reads it).
 *
 * ⚠️ **Ordering is a precondition, not a hope.** This is only meaningful after
 * `applyDocumentLocale()` has run, which every surface does synchronously in `main.tsx` before
 * `createRoot` — `scripts/assert-document-locale-wired.mjs` is what keeps that true. Called
 * earlier, or with no document at all (SSR, a node test), it falls back to `ru`, matching the
 * platform's RU-first default and the parse-time `lang="ru"` in every `index.html`.
 *
 * ⭐ The fallback is `ru` rather than `detectLocale()` deliberately: a reader that silently
 * substitutes a second decision when the first one is missing would hide exactly the wiring bug
 * the assert script exists to catch.
 */
export function readDocumentLocale() {
    const lang = typeof document !== 'undefined' ? document.documentElement.lang : '';
    return lang.toLowerCase().startsWith('en') ? 'en' : 'ru';
}
//# sourceMappingURL=documentLocale.js.map